"use client"

import { useFormatter } from "@/hooks/use-formatter"
import * as React from "react"
import { useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { useAuthStore } from "@/stores/auth-store"
import { usePopupStackStore } from "@/stores/popup-stack-store"
import { getAppointments } from "@/lib/appointments-api"
import { useToastNotifier } from "@/hooks/use-toast-notifier"
import { needsAllAssignees, shouldRemind, type ReminderUser } from "@/lib/appointment-reminder-scope"
import type { Appointment } from "@/types"

// REM-01, REM-12: timing constants
const REFETCH_INTERVAL_MS = 5 * 60 * 1000  // 5 minutes
const LOOKAHEAD_WINDOW_MS = 16 * 60 * 1000 // 16 min window — catches appts already past T-15
const REMINDER_OFFSET_MS = 15 * 60 * 1000  // fire 15 min before scheduled_at

// Dismissed-reminder persistence (2026-07-27): the in-memory firedRef dies on
// reload, so an appointment still inside the lookahead window re-fired its
// toast on every refresh even after the user dismissed it. Dismissals now land
// in localStorage keyed by `${id}:${scheduled_at}` — rescheduling an
// appointment changes the key, so a moved appointment legitimately reminds
// again. Entries expire well after the appointment so the map stays tiny.
const DISMISSED_STORAGE_KEY = "appointment-reminders.dismissed.v1"
const DISMISSED_TTL_MS = 24 * 60 * 60 * 1000

const { track: trackPopup, untrack: untrackPopup } = usePopupStackStore.getState()

/** key → expiry epoch ms */
type DismissedMap = Record<string, number>

function reminderKey(apt: Appointment): string {
  return `${apt.id}:${apt.scheduled_at}`
}

function readDismissed(): DismissedMap {
  if (typeof window === "undefined") return {}
  try {
    const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as DismissedMap
    const now = Date.now()
    const pruned: DismissedMap = {}
    for (const [key, expiry] of Object.entries(parsed)) {
      if (typeof expiry === "number" && expiry > now) pruned[key] = expiry
    }
    return pruned
  } catch {
    return {}
  }
}

function markDismissed(apt: Appointment): void {
  if (typeof window === "undefined") return
  try {
    const map = readDismissed()
    map[reminderKey(apt)] =
      new Date(apt.scheduled_at).getTime() + DISMISSED_TTL_MS
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // best-effort — quota / privacy-mode failures degrade to session-only dedupe.
  }
}

/**
 * Appointments rebuild 88 — the staff upcoming-appointment popup is a PER-USER
 * preference, not a clinic feature flag (the flag it replaces was deleted with
 * the rest of the old vocabulary). It lives in localStorage keyed by user id,
 * so two people sharing a
 * front-desk browser do not mute each other, and the absence of a key means ON
 * — nobody loses the reminder because a device has never been toggled.
 *
 * Toggled from the user's own profile page.
 */
export function appointmentPopupStorageKey(userId: string | number): string {
  return `appointment-reminder-popup:${userId}`
}

const POPUP_PREF_EVENT = "dentex:appointment-popup-pref"

/** Write the preference and notify listeners in THIS tab as well. */
export function setAppointmentPopupEnabled(
  userId: string | number,
  enabled: boolean
): void {
  if (typeof window === "undefined") return
  try {
    const key = appointmentPopupStorageKey(userId)
    if (enabled) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, "off")
    }
  } catch {
    // Private mode / quota — the toggle simply does not persist.
  }
  // The `storage` event only fires in OTHER tabs; this keeps the scheduler in
  // the same tab honest too.
  window.dispatchEvent(new Event(POPUP_PREF_EVENT))
}

/**
 * Live read of the popup preference. `useSyncExternalStore` rather than state
 * + effect so the value is correct on the first render and follows a toggle
 * made in another tab (the `storage` event) or this one.
 */
export function useAppointmentPopupEnabled(
  userId: string | number | undefined
): boolean {
  const subscribe = React.useCallback((onChange: () => void) => {
    window.addEventListener("storage", onChange)
    window.addEventListener(POPUP_PREF_EVENT, onChange)
    return () => {
      window.removeEventListener("storage", onChange)
      window.removeEventListener(POPUP_PREF_EVENT, onChange)
    }
  }, [])

  const getSnapshot = React.useCallback(() => {
    if (userId == null || typeof window === "undefined") return true
    try {
      return window.localStorage.getItem(appointmentPopupStorageKey(userId)) !== "off"
    } catch {
      return true
    }
  }, [userId])

  return React.useSyncExternalStore(subscribe, getSnapshot, () => true)
}

export function AppointmentReminderScheduler() {
  const clinicFormat = useFormatter()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const currentUserId = useAuthStore((s) => s.user?.id)
  const currentUserRoles = useAuthStore((s) => s.user?.roles)
  const teamScope = useAuthStore(
    (s) => s.user?.feature_flags?.appointment_reminders_team_scope === true
  )
  // Radiance 260930: the owner alone — wins over team scope and the role matrix.
  const ownerOnly = useAuthStore(
    (s) => s.user?.feature_flags?.appointment_reminders_owner_only === true
  )
  // Joined to a string so the effect below re-arms only when the set changes,
  // not on every /auth/me refresh that hands back a new array.
  const ledMemberKey = useAuthStore((s) => (s.user?.led_team_member_ids ?? []).join(","))
  const remindersEnabled = useAuthStore((s) => s.user?.feature_flags?.appointment_reminders_enabled ?? true)
  // 88: per-user preference (localStorage), not a clinic flag any more.
  const popupEnabled = useAppointmentPopupEnabled(currentUserId)
  // Task 4D: clinic-level opt-in to appointment typing. OFF → pre-Task-4D
  // behavior; ON → role/type-aware reminder scoping (see shouldRemind).
  const typesEnabled = useAuthStore((s) => s.user?.feature_flags?.typed_events_enabled === true)
  const router = useRouter()
  const notify = useToastNotifier()
  const t = useTranslations("appointments")
  const tc = useTranslations("common")

  // The staff toast is gated by the user's own popup preference AND the clinic
  // master reminders flag: turning the master flag off (which also stops the
  // patient WhatsApp reminders) still suppresses the popup, while the per-user
  // preference lets ONE person silence their own toast and keep WhatsApp
  // reminders running for everybody.
  const showPopup = remindersEnabled && popupEnabled

  // REM-02, REM-12: pending timers keyed by appointment id (Appointment.id is string per types/index.ts)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // REM-12: session-level dedupe of appointments we've already fired for
  const firedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Gate inside the effect (not via an early return before hooks) so the
    // hook order stays stable when the flag/auth state changes.
    if (!showPopup || !isAuthenticated || currentUserId == null) {
      // REM-08: clean everything on logout / when disabled
      timersRef.current.forEach(clearTimeout)
      timersRef.current.clear()
      firedRef.current.clear()
      return
    }

    let cancelled = false

    const fireReminder = (apt: Appointment) => {
      // REM-12: guard against double-fire
      if (firedRef.current.has(reminderKey(apt))) return
      firedRef.current.add(reminderKey(apt))

      const scheduledDate = new Date(apt.scheduled_at)
      const timeStr = clinicFormat.dateTime(scheduledDate, {
        hour: "2-digit",
        minute: "2-digit",
      })
      const leadName = apt.lead?.full_name ?? t("reminderToast.unknownLead")
      const viewHref =
        apt.lead_id != null ? `/leads/${apt.lead_id}` : `/calendar`

      // REM-03, REM-04, REM-05, REM-06: sticky toast + audio chime routed
      // through the shared useToastNotifier hook (extracted in Plan 38-01).
      // Hook defaults soundUrl to '/sounds/reminder.mp3' at volume 0.6, which
      // is exactly what Phase 37 shipped — no soundUrl override needed.
      // Closing the toast (X/swipe) or following its action persists the
      // dismissal so a page refresh doesn't resurrect the reminder.
      // Registered in the shared popup stack so the "Dismiss all" pill beside
      // the lead cards closes reminders too (Radiance 260924).
      const toastId = notify({
        title: t("reminderToast.title", { title: apt.display_title || t("reminderToast.fallbackTitle") }),
        description: `${leadName} — ${timeStr}`,
        actionLabel: tc("view"),
        onAction: () => {
          markDismissed(apt)
          untrackPopup(toastId)
          router.push(viewHref)
        },
        onDismiss: () => {
          markDismissed(apt)
          untrackPopup(toastId)
        },
      })
      trackPopup(toastId, () => markDismissed(apt))
    }

    const reminderUser: ReminderUser = {
      id: currentUserId,
      roles: currentUserRoles,
      ownerOnly,
      teamScope,
      ledMemberIds: ledMemberKey ? ledMemberKey.split(",") : [],
    }

    const armTimers = async () => {
      try {
        const now = new Date()
        const windowEnd = new Date(now.getTime() + LOOKAHEAD_WINDOW_MS)
        // REM-01, REM-11: fetch scheduled appts in the lookahead window. By
        // default we scope to the current user (Task 4D flag OFF, or a
        // doctor/agent when ON). Frontdesk + everything-access roles (flag ON)
        // need every assignee's appointments, so we drop the user_id filter for
        // them and let shouldRemind() do the client-side scoping below.
        const response = await getAppointments({
          // User.id is typed string, runtime number — see Pitfall 1 in 37-RESEARCH.md
          ...(needsAllAssignees(reminderUser, typesEnabled)
            ? {}
            : { user_id: String(currentUserId) }),
          status: "scheduled",
          date_from: now.toISOString(),
          date_to: windowEnd.toISOString(),
          per_page: 50,
        })
        if (cancelled) return

        // Fresh read each cycle so a dismissal in another tab propagates here
        // by the next refetch at the latest.
        const dismissed = readDismissed()

        // WR-01, WR-02: reconcile armed timers against the fresh response.
        // Build the set of ids that are still valid for this agent (scheduled,
        // matches currentUserId, and present in the lookahead window). Any
        // armed timer whose id is NOT in this set represents an appointment
        // that has been cancelled, deleted, rescheduled outside the window,
        // or otherwise is no longer something we should fire a reminder for.
        // We clearTimeout + delete those handles; the arming loop below will
        // then re-add any id that is still valid (which picks up fresh
        // title/lead/scheduled_at data on next tick if needed).
        const validIds = new Set<string>()
        for (const apt of response.data) {
          if (apt.status !== "scheduled") continue
          // Task 4D: role/type-aware scoping. Must mirror the arming loop below
          // so reconciliation tears down exactly the timers arming would skip.
          if (!shouldRemind(apt, reminderUser, typesEnabled)) continue
          if (dismissed[reminderKey(apt)]) continue
          validIds.add(apt.id)
        }
        for (const [id, handle] of timersRef.current) {
          if (!validIds.has(id)) {
            clearTimeout(handle)
            timersRef.current.delete(id)
          }
        }

        for (const apt of response.data) {
          // REM-11: defensive status check in case backend returns mixed statuses
          if (apt.status !== "scheduled") continue

          // Task 4D: role/type-aware scoping (see shouldRemind's matrix). On the
          // flag-OFF path this reduces to the original own-appointments-only
          // guard (T-37-01 mitigation + Pitfall 1 string coercion live inside
          // shouldRemind), so pre-Task-4D behavior is unchanged.
          if (!shouldRemind(apt, reminderUser, typesEnabled)) continue

          // Persisted dismissal — the user already closed this reminder in
          // some session; don't re-arm it after a refresh.
          if (dismissed[reminderKey(apt)]) continue

          // REM-12: skip already-armed or already-fired appointments
          if (timersRef.current.has(apt.id)) continue
          if (firedRef.current.has(reminderKey(apt))) continue

          const scheduledMs = new Date(apt.scheduled_at).getTime()
          const fireAt = scheduledMs - REMINDER_OFFSET_MS
          // REM-10: negative delays clamp to 0 → fire immediately
          const delay = Math.max(0, fireAt - Date.now())

          const handle = setTimeout(() => {
            timersRef.current.delete(apt.id)
            fireReminder(apt)
          }, delay)
          timersRef.current.set(apt.id, handle)
        }
      } catch (err) {
        console.error("[AppointmentReminderScheduler] fetch failed", err)
      }
    }

    // REM-01, REM-07: initial arm on mount
    void armTimers()
    // REM-01, REM-12: 5-min refetch
    const interval = setInterval(armTimers, REFETCH_INTERVAL_MS)

    // REM-08: cleanup on unmount / auth change / token change
    // Snapshot timersRef.current into a local variable so the cleanup function
    // captures the value at the time the effect runs (react-hooks/exhaustive-deps).
    const timersSnapshot = timersRef.current
    return () => {
      cancelled = true
      clearInterval(interval)
      timersSnapshot.forEach(clearTimeout)
      timersSnapshot.clear()
      // NOTE: intentionally preserve firedRef across the same session so
      // the 5-min refetch doesn't refire. It IS cleared in the early-
      // return branch above when the user logs out.
    }
  }, [showPopup, isAuthenticated, currentUserId, currentUserRoles, typesEnabled, ownerOnly, teamScope, ledMemberKey, router, notify, t, tc, clinicFormat])

  // TODO(phase-37+): revisit Sonner visibleToasts default (3) if agents
  // regularly see >3 concurrent reminders in the 15-minute window.
  return null
}
